import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import PixelGlyph from "./PixelGlyph";
import { useWindowManager } from "./WindowManager";
import {
  TASKBAR_HEIGHT,
  beginDrag,
  clamp,
  useIsDesktop,
} from "../utils/desktop";

// Minimum strip of title bar kept on-screen so the window can always be grabbed back.
const GRAB_MARGIN = 100;
const TITLE_BAR_HEIGHT = 32;
const CASCADE_STEP = 24;

// Handles sit inside the window's outer edge (offset by its 0.12rem border) so they stay fully
// grabbable when the window meets the screen edge.
const RESIZE_HANDLES = [
  { dir: "n", className: "top-[-0.12rem] left-3 right-3 h-[6px] cursor-ns-resize" },
  { dir: "s", className: "bottom-[-0.12rem] left-3 right-3 h-[6px] cursor-ns-resize" },
  { dir: "w", className: "left-[-0.12rem] top-3 bottom-3 w-[6px] cursor-ew-resize" },
  { dir: "e", className: "right-[-0.12rem] top-3 bottom-3 w-[6px] cursor-ew-resize" },
  { dir: "nw", className: "top-[-0.12rem] left-[-0.12rem] h-3 w-3 cursor-nwse-resize" },
  { dir: "ne", className: "top-[-0.12rem] right-[-0.12rem] h-3 w-3 cursor-nesw-resize" },
  { dir: "sw", className: "bottom-[-0.12rem] left-[-0.12rem] h-3 w-3 cursor-nesw-resize" },
  { dir: "se", className: "bottom-[-0.12rem] right-[-0.12rem] h-3 w-3 cursor-nwse-resize" },
];

const GLYPHS = {
  minimize: [
    ".........",
    ".........",
    ".........",
    ".........",
    ".........",
    ".........",
    ".........",
    ".######..",
    ".######..",
  ],
  maximize: [
    "#########",
    "#########",
    "#.......#",
    "#.......#",
    "#.......#",
    "#.......#",
    "#.......#",
    "#.......#",
    "#########",
  ],
  restore: [
    "..######.",
    "..######.",
    "..#....#.",
    "######.#.",
    "######.#.",
    "#....###.",
    "#....#...",
    "#....#...",
    "######...",
  ],
  close: [
    ".........",
    ".##....##",
    "..##..##.",
    "...####..",
    "....##...",
    "...####..",
    "..##..##.",
    ".##....##",
    ".........",
  ],
};

const desktopHeight = () => window.innerHeight - TASKBAR_HEIGHT;

const FIT_GAP = 8;

// The open desktop between the left and right icon columns, from the top of the screen to the
// taskbar, inset by a small gap. Icons are measured by their visible parts (image and label), so
// moved or hidden icons are accounted for.
const desktopFitRect = () => {
  const vw = window.innerWidth;
  let left = 0;
  let right = vw;
  document.querySelectorAll("[data-desktop-icon]").forEach((icon) => {
    const parts = [...icon.children].map((child) => child.getBoundingClientRect());
    const l = Math.min(...parts.map((b) => b.left));
    const r = Math.max(...parts.map((b) => b.right));
    if ((l + r) / 2 < vw / 2) {
      left = Math.max(left, r);
    } else {
      right = Math.min(right, l);
    }
  });
  return {
    x: left + FIT_GAP,
    y: FIT_GAP,
    w: right - left - 2 * FIT_GAP,
    h: desktopHeight() - 2 * FIT_GAP,
  };
};

const clampRect = ({ x, y, w, h }, minSize) => {
  const vw = window.innerWidth;
  const vh = desktopHeight();
  w = Math.max(minSize.w, Math.min(w, vw));
  h = Math.max(minSize.h, Math.min(h, vh));
  return {
    x: clamp(x, GRAB_MARGIN - w, vw - GRAB_MARGIN),
    y: clamp(y, 0, vh - TITLE_BAR_HEIGHT),
    w,
    h,
  };
};

// Edges never grow past the screen, but a window already past it may still shrink.
const resizeRect = (r0, dir, dx, dy, minSize) => {
  const vw = window.innerWidth;
  const vh = desktopHeight();
  let { x, y, w, h } = r0;

  if (dir.includes("e")) {
    w = clamp(r0.w + dx, minSize.w, Math.max(r0.w, vw - r0.x));
  }
  if (dir.includes("w")) {
    w = clamp(r0.w - dx, minSize.w, Math.max(r0.w, r0.x + r0.w));
    x = r0.x + r0.w - w;
  }
  if (dir.includes("s")) {
    h = clamp(r0.h + dy, minSize.h, Math.max(r0.h, vh - r0.y));
  }
  if (dir.includes("n")) {
    h = clamp(r0.h - dy, minSize.h, Math.max(r0.h, r0.y + r0.h));
    y = r0.y + r0.h - h;
  }
  return { x, y, w, h };
};

const cascadeRect = ({ w, h }, index, minSize) => {
  const offset = CASCADE_STEP * index;
  return clampRect(
    {
      x: (window.innerWidth - w) / 2 + offset,
      y: (desktopHeight() - h) / 2 + offset,
      w,
      h,
    },
    minSize
  );
};

const TitleButton = ({ label, glyph, onClick, className = "" }) => (
  <button
    className={`w-4 h-4 flex items-center justify-center bg-[#c0c0c0] border-[0.13rem] border-t-white border-l-white border-b-black border-r-black active:border-t-black active:border-l-black active:border-b-white active:border-r-white ${className}`}
    aria-label={label}
    title={label}
    onClick={onClick}
  >
    <PixelGlyph rows={glyph} />
  </button>
);

const Window98 = ({ app }) => {
  const {
    id,
    title,
    icon,
    buttons,
    minSize,
    defaultSize,
    showOnMobile,
    fixedSize,
    fitToDesktop,
    startMaximized,
    component: Content,
  } = app;
  const isDesktop = useIsDesktop();
  const { state, activate, minimize, close } = useWindowManager();
  // null until first moved/resized for apps without a defaultSize: the window keeps its centered CSS layout.
  const [rect, setRect] = useState(() =>
    defaultSize ? cascadeRect(defaultSize, state.open.indexOf(id), minSize) : null
  );
  const [maximized, setMaximized] = useState(Boolean(startMaximized));
  // Once the user moves or resizes the window, stop refitting it to the desktop.
  const [userPlaced, setUserPlaced] = useState(false);
  const windowRef = useRef(null);

  // Layout effect so the fitted size is in place before the first paint. Refit once fonts load, since
  // icon labels change width, and on browser resize.
  useLayoutEffect(() => {
    if (!fitToDesktop || !isDesktop || userPlaced) return;
    let active = true;
    const fit = () => active && setRect(clampRect(desktopFitRect(), minSize));
    fit();
    document.fonts?.ready.then(fit);
    window.addEventListener("resize", fit);
    return () => {
      active = false;
      window.removeEventListener("resize", fit);
    };
  }, [fitToDesktop, isDesktop, userPlaced, minSize]);

  // Clamping against a mobile viewport would scatter windows; reclamp only once the desktop is back.
  useEffect(() => {
    if (!isDesktop) return;
    const onResize = () => setRect((r) => r && clampRect(r, minSize));
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isDesktop, minSize]);

  const canMaximize = buttons.includes("maximize");
  const movable = isDesktop && !maximized;

  const startRect = () => {
    if (rect) return rect;
    const b = windowRef.current.getBoundingClientRect();
    return clampRect({ x: b.left, y: b.top, w: b.width, h: b.height }, minSize);
  };

  const trackDrag = (e, compute) => {
    const r0 = startRect();
    beginDrag(
      e,
      (dx, dy) => {
        setUserPlaced(true);
        setRect(compute(r0, dx, dy));
      },
      () => {}
    );
  };

  const handleTitlePointerDown = (e) => {
    if (!movable || e.target.closest("button")) return;
    trackDrag(e, (r0, dx, dy) => clampRect({ ...r0, x: r0.x + dx, y: r0.y + dy }, minSize));
  };

  const handleTitleDoubleClick = (e) => {
    if (!isDesktop || !canMaximize || e.target.closest("button")) return;
    setMaximized((m) => !m);
  };

  const focused = state.focused === id;
  // Minimizing is a desktop concept; on mobile the portfolio is the whole page and must stay visible.
  const hidden = isDesktop ? state.minimized.includes(id) : !showOnMobile;

  let layoutClass;
  let style = { zIndex: state.stack.indexOf(id) };
  if (isDesktop && maximized) {
    layoutClass = "fixed";
    style = { ...style, left: 0, top: 0, right: 0, bottom: TASKBAR_HEIGHT };
  } else if (isDesktop && rect) {
    layoutClass = "fixed";
    style = { ...style, left: rect.x, top: rect.y, width: rect.w, height: rect.h };
  } else {
    layoutClass = `relative
      w-full h-full  /* Default mobile styles */
      lg:w-[62vw] lg:h-[82.5vh] lg:max-w-[1000px] lg:max-h-[900px]
      mx-auto lg:min-w-[600px] lg:min-h-[600px]`;
  }

  return (
    <div
      ref={windowRef}
      className={`pointer-events-auto border-[0.12rem] border-[#c0c0c0] border-r-black border-b-black ${layoutClass} ${
        hidden ? "hidden" : ""
      }`}
      style={style}
      onPointerDownCapture={() => activate(id)}
    >
      {movable &&
        !fixedSize &&
        RESIZE_HANDLES.map(({ dir, className }) => (
          <div
            key={dir}
            className={`absolute z-20 select-none touch-none ${className}`}
            onPointerDown={(e) =>
              trackDrag(e, (r0, dx, dy) => resizeRect(r0, dir, dx, dy, minSize))
            }
          />
        ))}

      {/* Entire Window */}
      <div
        className="relative bg-[#c0c0c0] border-[0.12rem] border-t-white border-l-white border-b-[#828282] border-r-[#828282] w-full h-full"
      >
        {/* Title Bar */}
        <div
          className={`absolute top-[0.2rem] left-[0.2rem] right-[0.2rem] h-[1.5rem] flex items-center justify-between pl-[0.17rem] pr-[0.26rem] select-none touch-none ${
            focused ? "bg-[#00007F] text-white" : "bg-[#808080] text-[#c0c0c0]"
          }`}
          onPointerDown={handleTitlePointerDown}
          onDoubleClick={handleTitleDoubleClick}
        >
          <div className="flex items-center gap-1 min-w-0 desktop-font">
            <img src={icon} alt="Window Icon" className="h-4 w-4 shrink-0" draggable={false} />
            <span className="text-xl mt-[0.25rem] truncate">{title}</span>
          </div>

          {isDesktop && (
            <div className="flex items-center gap-[0.1rem] shrink-0">
              {buttons.includes("minimize") && (
                <TitleButton label="Minimize" glyph={GLYPHS.minimize} onClick={() => minimize(id)} />
              )}
              {canMaximize && (
                <TitleButton
                  label={maximized ? "Restore" : "Maximize"}
                  glyph={maximized ? GLYPHS.restore : GLYPHS.maximize}
                  onClick={() => setMaximized((m) => !m)}
                />
              )}
              {buttons.includes("close") && (
                <TitleButton
                  label="Close"
                  glyph={GLYPHS.close}
                  onClick={() => close(id)}
                  className="ml-[0.2rem]"
                />
              )}
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="absolute top-[2.05rem] left-[0.2rem] right-[0.2rem] bottom-[0.2rem] overflow-hidden">
          <Content />
        </div>
      </div>
    </div>
  );
};

export default Window98;
